import { useRef, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import styles from './MatchTicker.module.css'

export interface MatchTickerTeam {
  name: string
  logoUrl: string | null
  designation: string
  score: number | null
}

export interface MatchTickerEvent {
  id: string
  teams: MatchTickerTeam[]
  date: string
  status: 'scheduled' | 'live' | 'completed' | 'cancelled' | 'forfeit'
  game: string
  division?: string
  bestOf?: number
  isStreamed?: boolean
  /** The time isn't set yet; `date` is a placeholder and isn't shown. */
  timeTbd?: boolean
}

export interface MatchTickerProps {
  events: MatchTickerEvent[]
  onEventClick?: (event: MatchTickerEvent) => void
  /** Controls shown in a box at the start of the bar (e.g. game and division
   * filters). The box stays put while the matches scroll beside it. */
  scope?: ReactNode
  /** Eyebrow over the scope controls (default "Showing") */
  scopeLabel?: string
}

function formatMatchTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function sortEvents(events: MatchTickerEvent[]): MatchTickerEvent[] {
  const order: Record<string, number> = { live: 0, scheduled: 1, completed: 2, cancelled: 3, forfeit: 3 }
  return [...events].sort((a, b) => {
    /* v8 ignore next -- rank 4 is the fallback for a status outside the known ordering set */
    const oa = order[a.status] ?? 4
    const ob = order[b.status] ?? 4
    if (oa !== ob) return oa - ob
    if (a.status === 'completed') return new Date(b.date).getTime() - new Date(a.date).getTime()
    // Matches with a time set come first; a TBD one has no time to sort by.
    if (!!a.timeTbd !== !!b.timeTbd) return a.timeTbd ? 1 : -1
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })
}

const GAME_ABBREVS: Record<string, string> = {
  'League of Legends': 'LOL',
  'Valorant': 'VAL',
  'Rocket League': 'RL',
  'Counter-Strike': 'CS',
  'Overwatch': 'OW',
  'Apex Legends': 'APEX',
  'Fortnite': 'FN',
}

function getGameAbbrev(game: string): string {
  return GAME_ABBREVS[game] ?? game.slice(0, 3).toUpperCase()
}

export function MatchTicker({ events, onEventClick, scope, scopeLabel = 'Showing' }: MatchTickerProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Drag state refs (not in state to avoid re-renders during drag)
  const isDragging = useRef(false)
  const dragStartX = useRef(0)
  const scrollStartX = useRef(0)
  const hasDragged = useRef(false)

  const sorted = sortEvents(events)

  const updateArrows = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    updateArrows()
    el.addEventListener('scroll', updateArrows, { passive: true })
    window.addEventListener('resize', updateArrows)

    return () => {
      el.removeEventListener('scroll', updateArrows)
      window.removeEventListener('resize', updateArrows)
    }
  }, [updateArrows])

  // Reset scroll position when events change (e.g. filter changed upstream)
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    el.scrollLeft = 0
    requestAnimationFrame(updateArrows)
  }, [events, updateArrows])

  // Mouse drag-to-scroll for desktop
  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return
      isDragging.current = true
      hasDragged.current = false
      dragStartX.current = e.clientX
      scrollStartX.current = el!.scrollLeft
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDragging.current) return
      const dx = e.clientX - dragStartX.current
      if (Math.abs(dx) > 3) {
        hasDragged.current = true
        el!.style.scrollSnapType = 'none'
        el!.classList.add(styles.dragging)
        e.preventDefault()
      }
      el!.scrollLeft = scrollStartX.current - dx
    }

    function onMouseUp() {
      if (!isDragging.current) return
      isDragging.current = false
      el!.classList.remove(styles.dragging)
      el!.style.scrollSnapType = 'x mandatory'
    }

    el.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      el.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  function handleScroll(direction: 'left' | 'right') {
    trackRef.current?.scrollBy({
      left: direction === 'left' ? -300 : 300,
      behavior: 'smooth',
    })
  }

  function handleCardClick(event: MatchTickerEvent) {
    if (hasDragged.current) return
    onEventClick?.(event)
  }

  const scopeBox = scope != null && (
    <div className={styles.scope}>
      <span className={styles.scopeLabel}>{scopeLabel}</span>
      <div className={styles.scopeControls}>{scope}</div>
    </div>
  )

  if (events.length === 0) {
    // The scope box stays so a filter that empties the bar can be undone
    return (
      <div className={styles.ticker}>
        <div className={styles.carousel}>
          {scopeBox}
          <div className={styles.empty}>
            {/* An invisible, zero-width card holds the bar at a full card's
                height, so emptying it doesn't make the page jump */}
            <div className={`${styles.matchCard} ${styles.emptySizer}`} aria-hidden="true">
              <div className={styles.cardHeader}>Time TBD</div>
              <div className={styles.teams}>
                <div className={styles.teamRow}>TBD</div>
                <div className={styles.teamRow}>TBD</div>
              </div>
              <span className={styles.divisionLabel}>TBD</span>
            </div>
            No upcoming events — check back later
          </div>
        </div>
      </div>
    )
  }

  const showArrows = canScrollLeft || canScrollRight

  return (
    <div className={styles.ticker}>
      <div className={styles.carousel}>
        {scopeBox}
        <div className={styles.track} ref={trackRef}>
          {sorted.map(event => {
            const isCompleted = event.status === 'completed'
            const isLive = event.status === 'live'
            const teamA = event.teams[0]
            const teamB = event.teams[1]
            const hasScores = teamA?.score != null && teamB?.score != null
            /* v8 ignore next -- defensive default; the value is always present in practice */
            const aWins = isCompleted && hasScores && (teamA?.score ?? 0) > (teamB?.score ?? 0)
            /* v8 ignore next -- defensive default; the value is always present in practice */
            const bWins = isCompleted && hasScores && (teamB?.score ?? 0) > (teamA?.score ?? 0)
            // A decided result dims the loser; a tie dims neither.
            const decided = aWins || bWins
            const gameAbbrev = getGameAbbrev(event.game)
            const Card = onEventClick ? 'button' : 'div'

            return (
              <Card
                key={event.id}
                {...(onEventClick ? { type: 'button' as const } : {})}
                className={[
                  styles.matchCard,
                  isLive ? styles.matchCardLive : isCompleted ? styles.matchCardCompleted : styles.matchCardUpcoming,
                  onEventClick ? styles.matchCardClickable : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleCardClick(event)}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.statusSide}>
                    {isLive && <span className={styles.liveDot} />}
                    <span className={`${styles.statusLabel} ${
                      isLive ? styles.statusLive :
                      isCompleted ? styles.statusCompleted :
                      styles.statusUpcoming
                    }`}>
                      {isLive ? 'LIVE' : isCompleted ? 'FINAL' : event.timeTbd ? 'Time TBD' : formatMatchTime(event.date)}
                    </span>
                    {isLive && event.isStreamed && (
                      <span className={styles.streamBadge}>▶</span>
                    )}
                  </div>
                  <span className={styles.gameLabel}>{gameAbbrev}</span>
                </div>

                <div className={styles.teams}>
                  <div className={`${styles.teamRow} ${aWins ? styles.teamWinner : decided ? styles.teamLoser : ''}`}>
                    <span className={`${styles.teamName} ${teamA?.name ? '' : styles.teamTbd}`}>
                      {teamA?.name || 'TBD'}
                    </span>
                    {hasScores && <span className={styles.teamScore}>{teamA?.score}</span>}
                  </div>

                  <div className={`${styles.teamRow} ${bWins ? styles.teamWinner : decided ? styles.teamLoser : ''}`}>
                    <span className={`${styles.teamName} ${teamB?.name ? '' : styles.teamTbd}`}>
                      {teamB?.name || 'TBD'}
                    </span>
                    {hasScores && <span className={styles.teamScore}>{teamB?.score}</span>}
                  </div>
                </div>


                {event.division && <span className={styles.divisionLabel}>{event.division}</span>}
              </Card>
            )
          })}
        </div>

        {showArrows && (
          <div className={styles.arrows}>
            <button
              type="button"
              className={styles.arrow}
              onClick={() => handleScroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.arrow}
              onClick={() => handleScroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll right"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
